import React, { useRef, useState, useCallback, useEffect } from "react";
import ReactDOM from "react-dom";
import { usePortalPosition } from "../usePortalPosition";
import { PORTAL_ROOT_ID } from "../constants";
import styles from "./style.module.css";

const Tooltip = ({
  text,
  customStyles,
  children,
  position = "top",
  multiline = false,
  isTruncateMode = false,
  showOnClick = false,
}) => {
  const wrapperRef = useRef(null);
  const tooltipRef = useRef(null);
  const [anchorRect, setAnchorRect] = useState(null);
  const [isShouldShowTooltip, setIsShouldShowTooltip] = useState(true);

  const { portalStyle: tooltipStyle, resolvedPlacement: resolvedPosition } =
    usePortalPosition(anchorRect, tooltipRef, position, { align: "center" });

  const show = useCallback(() => {
    if (!wrapperRef.current) return;
    setAnchorRect(wrapperRef.current.getBoundingClientRect());
  }, []);

  const hide = useCallback(() => {
    setAnchorRect(null);
  }, []);

  // Click mode (mobile tap support): hide when tapping/clicking outside the wrapper.
  // The click handler shows (never toggles) — a tap fires mouseenter before click,
  // so a toggle would immediately hide what the mouseenter just showed.
  useEffect(() => {
    if (!showOnClick || !anchorRect) return undefined;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) hide();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showOnClick, anchorRect, hide]);

  useEffect(() => {
    // if isTruncateMode is enabled only show tooltip when text is truncated
    if (!isTruncateMode) return;
    if (!wrapperRef.current) return;

    const isOverflowing =
      wrapperRef.current.scrollWidth > wrapperRef.current.clientWidth;
    if (isOverflowing) {
      setIsShouldShowTooltip(true);
    } else {
      setIsShouldShowTooltip(false);
    }
  }, [isTruncateMode]);

  return (
    <div
      ref={wrapperRef}
      className={`${styles.wrapper} ${customStyles?.wrapper || ""}`}
      onMouseEnter={show}
      onMouseLeave={hide}
      onClick={showOnClick ? show : undefined}
    >
      {children}
      {anchorRect &&
        isShouldShowTooltip &&
        text &&
        ReactDOM.createPortal(
          <span
            ref={tooltipRef}
            className={`${styles.tooltip} ${styles[resolvedPosition]} ${multiline ? styles.multiline : ""} ${customStyles?.tooltip || ""}`}
            style={tooltipStyle}
          >
            {text}
          </span>,
          document.getElementById(PORTAL_ROOT_ID) || document.body,
        )}
    </div>
  );
};

export default Tooltip;
